import { useEffect, useRef } from 'react';
import { useCanvasStore } from './canvas-store';
import { mergeNativeConversation, nativeConversationMetadata, pendingNativeConversationKey } from './native-conversation-sync';
import type { CanvasChatMessage } from './canvas-types';

// This tab's cached canonical rows retain their first observed account owner.
// A cookie/account rotation cannot restart reads for another account's old
// pending rows, including after navigating away from the chat and back.
const observedOwners = new Map<string, number>();
const MAX_OBSERVED_TURNS = 1024;
function ownedPendingKey(projectId: number | null, accountId: number | null, messages: CanvasChatMessage[]) {
  if (!Number.isSafeInteger(projectId) || projectId! < 1 || !Number.isSafeInteger(accountId) || accountId! < 1) return '';
  return pendingNativeConversationKey(messages.filter(message => {
    const metadata = nativeConversationMetadata(message);
    if (!metadata || metadata.status !== 'running' || message.role !== 'assistant') return false;
    const turn = `${projectId}:${metadata.runId}`;
    if (!observedOwners.has(turn)) {
      // Fail closed instead of evicting another account's ownership binding.
      if (observedOwners.size >= MAX_OBSERVED_TURNS) return false;
      observedOwners.set(turn, accountId!);
    }
    return observedOwners.get(turn) === accountId;
  }));
}

/** Read-only recovery while the active account/chat has a pending desktop turn. */
export function useNativeConversationSync(projectId: number | null, accountId: number | null, messages: CanvasChatMessage[]) {
  const key = ownedPendingKey(projectId, accountId, messages);
  const scope = useRef({ projectId, accountId });
  scope.current = { projectId, accountId };
  useEffect(() => {
    if (!key || !Number.isSafeInteger(projectId) || projectId! < 1 || !Number.isSafeInteger(accountId) || accountId! < 1) return;
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let fetching = false;
    const controller = new AbortController();
    const valid = () => alive && scope.current.accountId === accountId && scope.current.projectId === projectId && useCanvasStore.getState().activeProjectId === projectId;
    async function read() {
      if (!valid() || fetching) return;
      if (document.visibilityState === 'hidden') { timer = setTimeout(read, 3000); return; }
      fetching = true;
      let retry = true;
      try {
        const response = await fetch(`/api/canvas/projects/${projectId}/conversation`, { credentials: 'include', cache: 'no-store', signal: AbortSignal.any([controller.signal, AbortSignal.timeout(15000)]) });
        if ([401, 403, 404].includes(response.status)) retry = false;
        if (response.ok) {
          const data = await response.json();
          if (valid() && Array.isArray(data?.history)) {
            const store = useCanvasStore.getState();
            const current = store.projects[projectId!]?.messages;
            if (current && ownedPendingKey(projectId, accountId, current) === key) {
              const next = mergeNativeConversation(current, data.history);
              if (next !== current) store._writeMessages(projectId!, () => next);
              retry = !!pendingNativeConversationKey(next);
            }
          }
        }
      } catch { /* A transient read failure never retries provider work. */ }
      finally {
        fetching = false;
        if (valid() && retry) timer = setTimeout(read, 3000);
      }
    }
    const visible = () => { if (document.visibilityState === 'visible') { if (timer) clearTimeout(timer); void read(); } };
    document.addEventListener('visibilitychange', visible);
    void read();
    return () => { alive = false; controller.abort(); if (timer) clearTimeout(timer); document.removeEventListener('visibilitychange', visible); };
  }, [projectId, accountId, key]);
}
